/**
 * todoClients — the active-client roster the to-do containers share.
 *
 * Both containers need it: the view container's SOURCE row picker, and the
 * create container's (which also needs it to *name* a pre-attached client —
 * a card modal passes only the client's card_id, so without the roster the
 * chip fell back to the literal word "Client").
 *
 * Module-scope cache so it is fetched at most once per page load rather than
 * per modal open. Roster changes mid-session are rare and a reload picks them
 * up — the same trade-off the assignee cache in engineTodosDataSource makes.
 */
import { operationsApi } from '../../services/api';
import type { OperationsClient } from '../../types';

let cache: OperationsClient[] | null = null;
let inFlight: Promise<OperationsClient[]> | null = null;

export function cachedTodoClients(): OperationsClient[] {
  return cache ?? [];
}

export async function loadTodoClientsOnce(): Promise<OperationsClient[]> {
  if (cache) return cache;
  if (inFlight) return inFlight;
  inFlight = (async () => {
    try {
      const res = await operationsApi.getClients('active');
      cache = Array.isArray(res?.data) ? res.data : [];
      return cache;
    } catch {
      // Fail-soft: an empty roster degrades the picker to "No active clients"
      // rather than breaking the container it lives in.
      cache = [];
      return cache;
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
}
